<script lang="ts">
	import classNames from 'classnames';
	import { fade, scale } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import { CrossIcon } from '../../assets/icons/CrossIcon';
	import { Txt, type TxtVariant } from '../Txt';
	import type { DialogStores } from './utils/createDialogStores';

	let {
		dialogStores,
		dataCy,
		showOverlay = true,
		title = undefined,
		description = undefined,
		shouldClose = true,
		locked = false,
		showCloseIcon = true,
		titleVariant = 'h3',
		onClose = undefined,
		separator = false,
		classes = {},
		children,
		...rest
	}: Props = $props();

	interface Props {
		class?: string;
		dialogStores: DialogStores;
		dataCy: string;
		showOverlay?: boolean;
		title?: string | undefined;
		description?: string | undefined;
		shouldClose?: boolean;
		/**
		 * Ignores every way of closing (overlay, Escape, close icon) without
		 * changing the layout, e.g. while a request started from the dialog runs.
		 */
		locked?: boolean;
		showCloseIcon?: boolean;
		titleVariant?: TxtVariant;
		onClose?: undefined | (() => void);
		separator?: boolean;
		children?: import('svelte').Snippet;
		classes?: {
			title?: string;
			description?: string;
			separator?: string;
			closeIcon?: string;
			closeIconButton?: string;
		};
	}

	let dialogClasses = $derived(
		twMerge(
			classNames(
				'fixed left-[50%] top-[50%] z-[60] overflow-auto max-h-[85vh] w-[90vw] max-w-[640px] rounded translate-x-[-50%] translate-y-[-50%] bg-[white] shadow-lg',
				{
					'px-[1rem] pb-[2.5rem] pt-[4rem] sm:p-[3rem]': shouldClose,
					'px-[1rem] py-[1.5rem] sm:p-[4rem]': !shouldClose
				}
			),
			rest.class
		)
	);

	let open = $derived(dialogStores.states.open);

	const handleClose = () => {
		if (locked) return;
		if (onClose) {
			onClose();
		}
		if (shouldClose) {
			open.set(false);
		}
	};
</script>

<svelte:window
	onkeydown={(e) => {
		if (e.key === 'Escape' && $open && shouldClose) {
			handleClose();
		}
	}}
/>

<div data-cy={`${dataCy}-dialog`}>
	{#if $open}
		{#if showOverlay}
			{#if shouldClose}
				<button
					type="button"
					aria-label="Close dialog"
					onclick={handleClose}
					class="fixed inset-0 z-50 bg-[black]/50"
					transition:fade={{ duration: 150 }}
				></button>
			{:else}
				<div class="fixed inset-0 z-50 bg-[black]/50" transition:fade={{ duration: 150 }}></div>
			{/if}
		{/if}

		<div
			class={dialogClasses}
			role="dialog"
			aria-modal="true"
			aria-label={title}
			transition:scale={{
				duration: 150,
				start: 0.96
			}}
		>
			{#if title}
				<Txt
					variant={titleVariant}
					class={twMerge(
						'px-3 text-center',
						classNames({
							'mb-2': !!description,
							'mb-4': separator && !description,
							'mb-4 sm:mb-6': !description && !separator
						}),
						classes.title
					)}
				>
					{title}
				</Txt>
			{/if}
			{#if separator}
				<hr class={twMerge('mb-[2.5rem]', classes.separator)} />
			{/if}

			{#if description}
				<Txt variant="body1" class={twMerge('mb-8 text-center', classes.description)}>
					{description}
				</Txt>
			{/if}

			{@render children?.()}

			{#if showCloseIcon}
				<button
					onclick={handleClose}
					disabled={locked}
					aria-label="close"
					class={twMerge(
						'absolute right-2 top-2 inline-flex appearance-none items-center justify-center rounded-full p-1',
						classes.closeIconButton
					)}
				>
					<CrossIcon class={twMerge('h-6 w-5', classes.closeIcon)} />
				</button>
			{/if}
		</div>
	{/if}
</div>
